import httpClient from '@xver/libs/axios';
import {
    ActivityPage,
    CreateProjectInvitationRequest,
    CreateProjectRequest,
    ListResponse,
    MoveResourcesRequest,
    MoveResourcesResponse,
    ProjectDetailModel,
    ProjectInvitationModel,
    ProjectMemberModel,
    ProjectModel,
    ProjectRequestsModel,
    ProjectResourcesModel,
    SetProjectPayerRequest,
    TransferAcceptResponse,
    TransferProjectRequest,
    UpdateProjectMemberRequest,
    UpdateProjectRequest,
} from './model';

const ENDPOINTS = {
    LIST: '/v1/projects',
    ONE: (id: string) => `/v1/projects/${id}`,
    LEAVE: (id: string) => `/v1/projects/${id}/leave`,
    ACTIVITY: (id: string) => `/v1/projects/${id}/activity`,
    MEMBERS: (id: string) => `/v1/projects/${id}/members`,
    MEMBER: (id: string, userId: number) => `/v1/projects/${id}/members/${userId}`,
    INVITATIONS: (id: string) => `/v1/projects/${id}/invitations`,
    INVITATION: (id: string, invId: number) => `/v1/projects/${id}/invitations/${invId}`,
    REQUESTS: '/v1/project-requests',
    ACCEPT_INVITATION: (invId: number) => `/v1/project-invitations/${invId}/accept`,
    DECLINE_INVITATION: (invId: number) => `/v1/project-invitations/${invId}/decline`,
    PAYER: (id: string) => `/v1/projects/${id}/payer`,
    PAYER_ACCEPT: (id: string) => `/v1/projects/${id}/payer/accept`,
    PAYER_DECLINE: (id: string) => `/v1/projects/${id}/payer/decline`,
    TRANSFER: (id: string) => `/v1/projects/${id}/transfer`,
    TRANSFER_ACCEPT: (id: string) => `/v1/projects/${id}/transfer/accept`,
    TRANSFER_DECLINE: (id: string) => `/v1/projects/${id}/transfer/decline`,
    RESOURCES: (id: string) => `/v1/projects/${id}/resources`,
    MOVE_RESOURCES: (id: string) => `/v1/projects/${id}/resources/move`,
};

export function listProjects(): Promise<ListResponse<ProjectModel>> {
    return httpClient.get(ENDPOINTS.LIST);
}

export function createProject(data: CreateProjectRequest): Promise<ProjectModel> {
    return httpClient.post(ENDPOINTS.LIST, data);
}

export function getProject(id: string): Promise<ProjectDetailModel> {
    return httpClient.get(ENDPOINTS.ONE(id));
}

export function updateProject(id: string, data: UpdateProjectRequest): Promise<ProjectModel> {
    return httpClient.put(ENDPOINTS.ONE(id), data);
}

export function deleteProject(id: string): Promise<void> {
    return httpClient.delete(ENDPOINTS.ONE(id));
}

export function leaveProject(id: string): Promise<void> {
    return httpClient.post(ENDPOINTS.LEAVE(id));
}

export function listActivity(projectId: string, cursor?: number): Promise<ActivityPage> {
    return httpClient.get(ENDPOINTS.ACTIVITY(projectId), {
        params: cursor === undefined ? undefined : { cursor },
    });
}

export function listMembers(id: string): Promise<ListResponse<ProjectMemberModel>> {
    return httpClient.get(ENDPOINTS.MEMBERS(id));
}

export function updateMember(
    id: string,
    userId: number,
    data: UpdateProjectMemberRequest
): Promise<ProjectMemberModel> {
    return httpClient.put(ENDPOINTS.MEMBER(id, userId), data);
}

export function removeMember(id: string, userId: number): Promise<void> {
    return httpClient.delete(ENDPOINTS.MEMBER(id, userId));
}

export function createInvitation(
    id: string,
    data: CreateProjectInvitationRequest
): Promise<ProjectInvitationModel> {
    return httpClient.post(ENDPOINTS.INVITATIONS(id), data);
}

export function listInvitations(id: string): Promise<ListResponse<ProjectInvitationModel>> {
    return httpClient.get(ENDPOINTS.INVITATIONS(id));
}

export function revokeInvitation(id: string, invId: number): Promise<void> {
    return httpClient.delete(ENDPOINTS.INVITATION(id, invId));
}

export function listProjectRequests(): Promise<ProjectRequestsModel> {
    return httpClient.get(ENDPOINTS.REQUESTS);
}

export function acceptInvitation(invId: number): Promise<ProjectModel> {
    return httpClient.post(ENDPOINTS.ACCEPT_INVITATION(invId));
}

export function declineInvitation(invId: number): Promise<void> {
    return httpClient.post(ENDPOINTS.DECLINE_INVITATION(invId));
}

export function setPayer(id: string, data: SetProjectPayerRequest): Promise<ProjectModel> {
    return httpClient.put(ENDPOINTS.PAYER(id), data);
}

export function acceptPayer(id: string): Promise<ProjectModel> {
    return httpClient.post(ENDPOINTS.PAYER_ACCEPT(id));
}

export function declinePayer(id: string): Promise<void> {
    return httpClient.post(ENDPOINTS.PAYER_DECLINE(id));
}

export function offerTransfer(id: string, data: TransferProjectRequest): Promise<ProjectModel> {
    return httpClient.post(ENDPOINTS.TRANSFER(id), data);
}

export function cancelTransfer(id: string): Promise<void> {
    return httpClient.delete(ENDPOINTS.TRANSFER(id));
}

export function acceptTransfer(id: string): Promise<TransferAcceptResponse> {
    return httpClient.post(ENDPOINTS.TRANSFER_ACCEPT(id));
}

export function declineTransfer(id: string): Promise<void> {
    return httpClient.post(ENDPOINTS.TRANSFER_DECLINE(id));
}

export function listResources(id: string): Promise<ProjectResourcesModel> {
    return httpClient.get(ENDPOINTS.RESOURCES(id));
}

export function moveResources(
    id: string,
    data: MoveResourcesRequest
): Promise<MoveResourcesResponse> {
    return httpClient.post(ENDPOINTS.MOVE_RESOURCES(id), data);
}
